import React, {
	type ComponentRef,
	type ComponentType,
	type FC,
	forwardRef,
	type PropsWithoutRef,
	type PropsWithRef,
	type RefAttributes,
} from 'react';

import Button, { type ButtonProps, IconButton, type IconButtonProps } from '@atlaskit/button/new';
import { fg } from '@atlaskit/platform-feature-flags';

import {
	ThemedButton,
	type ThemedButtonProps,
	ThemedIconButton,
	type ThemedIconButtonProps,
} from './button';

/**
 * `componentWithCondition` util was copied from
 * `jira/src/packages/platform/utils/feature-flagging-utils/src/utils/component-with-condition/index.tsx`
 */

/**
 * Gets all available ref types from two prop sets and returns
 * them in a ref prop
 *
 * @example
 * ```
 * type P1 = { ref: Ref(HTMLDivElement), ... };
 * type P2 = { ref: Ref(HTMLSpanElement), ... };
 *
 * GetRefAttributes(P1, P2) // {ref: Ref(HTMLDivElement | HTMLSpanElement)}
 * ```
 */
type GetRefAttributes<A, B> =
	A extends RefAttributes<infer RefA>
		? B extends RefAttributes<infer RefB>
			? RefAttributes<RefA | RefB>
			: RefAttributes<RefA>
		: B extends RefAttributes<infer OnlyRefB>
			? RefAttributes<OnlyRefB>
			: unknown;

/**
 * Returns one of components depending on a boolean condition.
 * The result component will be a union of the two props and
 * an or on both ref types.
 *
 * @example
 * ```
 * const Component = componentWithCondition(
 *      isBooleanConditionMet,
 *      ComponentWithConditionMet,
 *      ComponentWithConditionNotMet,
 * );
 * ```
 *
 * @param condition Function returning boolean value
 * @param componentTrue Component that will be returned if conditionGetter is "true"
 * @param componentFalse Component that will be returned if conditionGetter is "false"
 * @returns Component Depending on a Condition result
 */
function componentWithCondition<A extends {}, B extends {}>(
	condition: () => boolean,
	ComponentTrue: ComponentType<A>,
	ComponentFalse: ComponentType<B>,
) {
	const ComponentWithCondition = forwardRef<
		ComponentRef<typeof ComponentTrue | typeof ComponentFalse>,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		any
	>((props, ref) =>
		condition() ? <ComponentTrue {...props} ref={ref} /> : <ComponentFalse {...props} ref={ref} />,
	);
	if (ComponentTrue.name !== '') {
		ComponentWithCondition.displayName = `ComponentWithCondition[${condition.name}]`;
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return ComponentWithCondition as FC<
		PropsWithoutRef<A> & PropsWithoutRef<B> & GetRefAttributes<PropsWithRef<A>, PropsWithRef<B>>
	>;
}

// Needs an explicit type annotation or it results in types that reach into `/dist` and cause failures.
const IconButtonComponent:
	| React.ComponentType<ThemedIconButtonProps & React.RefAttributes<HTMLButtonElement>>
	| React.ComponentType<IconButtonProps> = componentWithCondition(
	() => fg('platform_design_system_nav4_top_bar_theming'),
	ThemedIconButton,
	IconButton,
);

// Needs an explicit type annotation or it results in types that reach into `/dist` and cause failures.
const ButtonComponent:
	| React.ComponentType<ThemedButtonProps & React.RefAttributes<HTMLButtonElement>>
	| React.ComponentType<ButtonProps> = componentWithCondition(
	() => fg('platform_design_system_nav4_top_bar_theming'),
	ThemedButton,
	Button,
);

export { IconButtonComponent as IconButton, ButtonComponent as Button };
